import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllAlbums, type AlbumData } from '@/services/albumService';

import { readAlbumSortMode as readSortMode, saveAlbumSortMode, sortAlbums, type AlbumSortMode } from '@/services/albumSort';

export default function AlbumsPage() {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState<AlbumData[]>([]);
  const [sortMode, setSortMode] = useState<AlbumSortMode>(readSortMode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sortedAlbums = useMemo(() => sortAlbums(albums, sortMode), [albums, sortMode]);

  useEffect(() => {
    saveAlbumSortMode(sortMode);
  }, [sortMode]);

  const loadAlbums = useCallback(async () => {
    try {
      const data = await getAllAlbums();
      setAlbums(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '앨범을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAlbums();
    const refresh = () => { void loadAlbums(); };
    window.addEventListener('family-upload-complete', refresh);
    return () => window.removeEventListener('family-upload-complete', refresh);
  }, [loadAlbums]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="px-4 pb-6 pt-12">
          <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">추억 보관함</span>
          <h1 className="mt-1 text-2xl font-bold leading-tight text-foreground-950">우리의<br />앨범</h1>
        </header>
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent-500 border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="px-4 pb-6 pt-12">
          <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">추억 보관함</span>
          <h1 className="mt-1 text-2xl font-bold leading-tight text-foreground-950">우리의<br />앨범</h1>
        </header>
        <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 rounded-full bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50">다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="px-4 pb-4 pt-12">
        <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">추억 보관함</span>
        <h1 className="mt-1 text-2xl font-bold leading-tight text-foreground-950">우리의<br />앨범</h1>
        <p className="mt-2 text-sm text-foreground-500">{albums.length}개의 앨범에 소중한 추억이 담겨있어요</p>
      </header>

      <div className="px-4 pb-3">
        <button
          onClick={() => navigate('/album/create')}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-dashed border-background-300/80 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-500 transition-all hover:border-accent-400 hover:text-accent-500"
        >
          <i className="ri-add-line text-lg" />
          새 앨범 만들기
        </button>
      </div>

      <div className="px-4 pb-4">
        <label className="flex items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-50 px-3 py-2.5">
          <span className="flex items-center gap-2 text-xs font-semibold text-foreground-600">
            <i className="ri-sort-asc text-base text-accent-500" />
            앨범 정렬
          </span>
          <select
            value={sortMode}
            onChange={(event) => setSortMode(event.target.value as AlbumSortMode)}
            className="min-w-0 flex-1 bg-transparent text-right text-xs font-medium text-foreground-800 outline-none"
          >
            <option value="title-asc">제목 오름차순</option>
            <option value="title-desc">제목 내림차순</option>
            <option value="capture-newest">최근 촬영일순</option>
            <option value="capture-oldest">오래된 촬영일순</option>
          </select>
        </label>
      </div>

      {sortedAlbums.length === 0 ? (
        <section className="flex flex-1 flex-col items-center justify-center px-4 py-16">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100">
            <i className="ri-gallery-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm text-foreground-500">아직 앨범이 없어요</p>
          <p className="mt-1 text-xs text-foreground-400">첫 번째 앨범을 만들어보세요</p>
        </section>
      ) : (
        <section className="px-4 pb-24">
          <div className="grid grid-cols-2 gap-3">
            {sortedAlbums.map((album) => (
              <div key={album.id} onClick={() => navigate(`/album/${album.id}`)} className="group cursor-pointer">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-secondary-100">
                  <img src={album.coverImage} alt={album.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-2.5">
                    <span className="text-[11px] font-medium text-background-50/90">{album.photoCount}장</span>
                  </div>
                </div>
                <div className="mt-2 px-0.5">
                  <h3 className="truncate text-sm font-bold text-foreground-900 transition-colors group-hover:text-accent-500">{album.title}</h3>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <i className="ri-calendar-check-line text-[11px] text-foreground-400" />
                    <span className="text-[11px] text-foreground-500">
                      {album.captureDate ? `촬영 ${album.captureDate}` : `생성 ${album.createdAt}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
